import { isDevelopment } from '@/lib/config';
import { safeRedirectPath } from '@/lib/utils/safeRedirectPath';

/**
 * The pending MCP OAuth authorization, remembered across a sign-in (#12168).
 *
 * GoTrue hands the browser to `/auth/mcp-consent?authorization_id=<id>`. A
 * signed-out visitor is bounced to `/sk/prihlasenie?redirect=<consent>` and
 * from there every sign-in leg threads the target along as a query param:
 * `redirect` → `next` on `/auth/callback` (OAuth, magic link, signup email) →
 * `next` on `/auth/accept-terms` → `redirect` on `/mfa/*`. Any hop that falls
 * back to `/` lands on `/sk/chat` (the root 308s there) with the connector
 * stranded: installed, tool-less, no error anywhere — exactly #12168. The
 * `/auth/callback` failure branch did that for every sign-in whose code
 * exchange failed, and for the emailed magic link, which reaches the callback
 * with no `code` at all; the sign-up link from the login card dropped it too.
 *
 * This module gives the authorization a second carrier that does not depend
 * on the query chain: a short-lived cookie holding ONLY the authorization id.
 * The consent interstitial writes it when it bounces to login and clears it
 * once the session gate passes; the sign-in and sign-up cards re-stamp it
 * before every leg that leaves the origin.
 *
 * It is honoured ONLY on the legs that lose the query chain — never for a
 * sign-in that simply started with no target. Otherwise a cookie left behind
 * by an abandoned (or an attacker's) consent link would, for ten minutes,
 * steer ANY later sign-in on that browser onto that consent screen (a
 * consent-phishing amplifier; /ck security review of #12219). The legs:
 *   - `/auth/callback` with no `code` (the `/auth/confirm` arrival), and
 *   - `/sk/prihlasenie?error=…` (the callback's failure recovery).
 * A consent reached through the cookie carries `resumed=1`, and the consent
 * screen then asks the user to confirm they started this connection, with
 * Deny given visual priority. Sign-out, the accept-terms decline and Deny
 * all clear the cookie.
 *
 * Open redirect: the cookie never holds a path. Its value must match
 * {@link AUTHORIZATION_ID_PATTERN} and the return target is built from the
 * fixed template in {@link mcpConsentReturnPath}, so nothing a browser can put
 * in the cookie reaches a navigation sink. A query-supplied target always wins
 * over the cookie and keeps going through `safeRedirectPath`.
 *
 * Not `HttpOnly` on purpose: the writer and the consumer that clears it are
 * client components. The id is already in the URL of every hop, and GoTrue
 * binds approve/deny to the signed-in user's own session, so knowing it grants
 * nothing. `SameSite=Lax` is enough: every return hop (Google → GoTrue →
 * `/auth/callback`, the emailed link → `/auth/confirm`) is a top-level GET.
 * On https the cookie carries the `__Host-` prefix (Secure, `Path=/`, no
 * `Domain`), so no other host under lexomat.sk can plant one; the server
 * accepts the unprefixed name only in local development, where there is no
 * TLS to carry the prefix.
 */

/** Same shape the backend accepts (`AUTHORIZATION_ID_PATTERN`, `mcp_consent_handoff.py`). */
const AUTHORIZATION_ID_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

export function isValidAuthorizationId(authorizationId: string): boolean {
  return AUTHORIZATION_ID_PATTERN.test(authorizationId);
}

/** The cookie as written over plain http (local development only). */
export const MCP_PENDING_AUTHORIZATION_COOKIE = 'mcp_pending_authorization';

/** The cookie as written over https: `__Host-` binds it to this exact host. */
export const MCP_PENDING_AUTHORIZATION_HOST_COOKIE = `__Host-${MCP_PENDING_AUTHORIZATION_COOKIE}`;

/**
 * GoTrue's `AUTHORIZATION_TTL` is 10 minutes
 * (https://supabase.com/docs/guides/auth/oauth-server/oauth-flows), so the
 * cookie never outlives the authorization it names.
 */
export const MCP_PENDING_AUTHORIZATION_MAX_AGE_SECONDS = 600;

/** Query marker on a consent URL reached through the cookie, not the query chain. */
const MCP_CONSENT_RESUMED_PARAM = 'resumed';

const MCP_CONSENT_PATH = '/auth/mcp-consent';

function pendingMcpAuthorizationCookieName(secure: boolean): string {
  return secure
    ? MCP_PENDING_AUTHORIZATION_HOST_COOKIE
    : MCP_PENDING_AUTHORIZATION_COOKIE;
}

/**
 * The remembered cookie value as the server reads it. Production accepts the
 * `__Host-` name only; the plain name is read in local development alone,
 * because a plain-named cookie can be planted by any host under the site's
 * domain and would be exactly the cross-host steering `__Host-` rules out.
 */
export function readPendingMcpAuthorizationCookie(
  get: (name: string) => string | undefined,
  development: boolean = isDevelopment
): string | undefined {
  const hostValue = get(MCP_PENDING_AUTHORIZATION_HOST_COOKIE);
  if (hostValue !== undefined) return hostValue;
  return development ? get(MCP_PENDING_AUTHORIZATION_COOKIE) : undefined;
}

/**
 * The consent URL a sign-in returns to: fixed path, the id, and
 * `session_check=1` — the #10911 loop marker, because every return through
 * this module is a round trip via `/sk/prihlasenie` (see `loginRedirectPath`
 * in `McpConsentInterstitial.tsx`). `resumed` adds the marker the consent
 * screen uses to ask "did you start this?".
 */
export function mcpConsentReturnPath(
  authorizationId: string,
  { resumed = false }: { resumed?: boolean } = {}
): string {
  const base = `${MCP_CONSENT_PATH}?authorization_id=${authorizationId}&session_check=1`;
  return resumed ? `${base}&${MCP_CONSENT_RESUMED_PARAM}=1` : base;
}

/**
 * The authorization id a consent path names, or `null` for anything else —
 * another route, a look-alike path, an off-origin or smuggled value, a
 * missing or malformed id.
 */
export function authorizationIdFromConsentPath(
  path: string | null | undefined
): string | null {
  const safe = safeRedirectPath(path, '');
  if (!safe.startsWith(`${MCP_CONSENT_PATH}?`)) return null;
  const id = new URLSearchParams(safe.slice(MCP_CONSENT_PATH.length + 1)).get(
    'authorization_id'
  );
  return id && isValidAuthorizationId(id) ? id : null;
}

/**
 * The resumed return path for a remembered cookie value, or `null` when it is
 * absent or not an id.
 */
export function pendingMcpConsentPath(
  cookieValue: string | null | undefined
): string | null {
  return cookieValue && isValidAuthorizationId(cookieValue)
    ? mcpConsentReturnPath(cookieValue, { resumed: true })
    : null;
}

/**
 * The post-auth target: `target` when the request carried one (already
 * sanitized by the caller), else the pending consent path marked `resumed`,
 * else `/`. The pending value is re-derived from its id so a caller mistake
 * cannot turn it into an open redirect. Callers decide WHETHER this leg may
 * fall back at all — see {@link resolveCallbackNext} and
 * {@link resolveSignInTarget}.
 */
export function withPendingMcpAuthorization(
  target: string,
  pending: string | null | undefined
): string {
  if (target !== '/') return target;
  const id = authorizationIdFromConsentPath(pending);
  return id ? mcpConsentReturnPath(id, { resumed: true }) : '/';
}

/**
 * Where `/auth/callback` sends the browser. The cookie fills in only on the
 * no-`code` arrival (the `/auth/confirm` leg); an OAuth code exchange keeps
 * its own `next` — GoTrue returns it intact (probed 2026-10-09) — so a plain
 * Google sign-in with a stale cookie still lands on `/`.
 */
export function resolveCallbackNext({
  next,
  code,
  cookieValue,
}: {
  /** The sanitized `next` query param (`'/'` when absent or rejected). */
  next: string;
  code: string | null;
  cookieValue: string | undefined;
}): string {
  if (next !== '/' || code) return next;
  return withPendingMcpAuthorization(next, pendingMcpConsentPath(cookieValue));
}

/**
 * Where `/sk/prihlasenie` returns to after sign-in. The cookie fills in only
 * while `recovering` — the page was reached with `?error=` from a failed
 * `/auth/callback`, the one leg that drops `redirect`. A sign-in started
 * with no target never inherits a stale cookie's consent.
 */
export function resolveSignInTarget({
  redirect,
  recovering,
  cookieValue,
}: {
  /** The sanitized `redirect` query param (`'/'` when absent or rejected). */
  redirect: string;
  recovering: boolean;
  cookieValue: string | undefined;
}): { target: string; pendingRedirect: string | null } {
  const pendingRedirect = recovering
    ? pendingMcpConsentPath(cookieValue)
    : null;
  return {
    target: withPendingMcpAuthorization(redirect, pendingRedirect),
    pendingRedirect,
  };
}

/** `Set-Cookie`-style string that remembers `authorizationId`. */
export function pendingMcpAuthorizationCookie(
  authorizationId: string,
  secure: boolean
): string {
  return `${pendingMcpAuthorizationCookieName(secure)}=${authorizationId}; Path=/; Max-Age=${MCP_PENDING_AUTHORIZATION_MAX_AGE_SECONDS}; SameSite=Lax${secure ? '; Secure' : ''}`;
}

/** `Set-Cookie`-style string that expires the cookie. */
export function expiredPendingMcpAuthorizationCookie(secure: boolean): string {
  return `${pendingMcpAuthorizationCookieName(secure)}=; Path=/; Max-Age=0; SameSite=Lax${secure ? '; Secure' : ''}`;
}

function writeDocumentCookie(cookie: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = cookie;
}

function isSecureContext(): boolean {
  return typeof window !== 'undefined' && window.location.protocol === 'https:';
}

/**
 * Remember the authorization in the browser. Ignores anything that is not an
 * authorization id, so a crafted `authorization_id` in the URL writes nothing.
 */
export function rememberPendingMcpAuthorization(
  authorizationId: string | null | undefined,
  write: (cookie: string) => void = writeDocumentCookie
): void {
  if (!authorizationId || !isValidAuthorizationId(authorizationId)) return;
  write(pendingMcpAuthorizationCookie(authorizationId, isSecureContext()));
}

/**
 * Remember the authorization a post-sign-in target names, if it is the
 * consent route; every other target leaves the cookie alone.
 */
export function rememberPendingMcpAuthorizationFor(
  target: string | null | undefined,
  write: (cookie: string) => void = writeDocumentCookie
): void {
  rememberPendingMcpAuthorization(
    authorizationIdFromConsentPath(target),
    write
  );
}

/**
 * Clear the remembered authorization — the consent page has it in hand, the
 * user signed out, declined the terms, or denied the request.
 */
export function forgetPendingMcpAuthorization(
  write: (cookie: string) => void = writeDocumentCookie
): void {
  write(expiredPendingMcpAuthorizationCookie(isSecureContext()));
}
